/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getPersonaFromMapping, defaultAccessKey, getAccessKey, setIntlConfig } from './utils/PersonaUtils';
import { ClientCommunicator } from '@alme/post-message-api/ClientCommunicator';
import { useAlmeMessageListener } from './hooks/useAlmeMessageListener';
import { useGetPersonaConfigCallback } from './hooks/useGetPersonaConfigCallback';
import { CookieLoader } from '@alme/storage-ts/CookieLoader';
import VerintIVAUI from './VerintIVAUI';
import defaultLogger from './Logger';
import { LiveChatNotificationApiSettings } from '@alme/api-ts/LiveChat/LiveChatNotificationApiSettings';
import { AlmeIvaTypeProp } from './models/AlmeIvaTypeMapping';
import appConfigMain from './personas/mainPersona/config';
import { AlmeConfig } from './models/AlmeConfig';
import { AlmeTheme, AlmeThemeMode } from './models/AlmeTheme';
import { createDefaultSettings } from './api/AlmeApi';
import appThemeMain from './personas/mainPersona/theme';
import AlmeTestPage from './components/AlmeTestPage/AlmeTestPage';
import { PersonaState } from './models/AlmePersona';
import { ClientConfig } from './models/ClientConfigMap';
import { RawIntlProvider } from 'react-intl';

import defaultTranslation from "./personas/mainPersona/translations/languages.json";

export interface IvaWrapperProps {
    renderTestPage?: boolean;
    theme?: AlmeTheme;
    themeMode?: AlmeThemeMode;
    almeConfig?: AlmeConfig;
    apiSettings?: LiveChatNotificationApiSettings;
    clientConfig?: ClientConfig;
    ivaType?: AlmeIvaTypeProp;
    verintAccessKey?: string;
    isIvaProLaunch?: React.MutableRefObject<boolean>;
}

const clientCommunicator = new ClientCommunicator();
const savedAccessKey = new CookieLoader<string>('verintAccessKey');

const IvaWrapper: React.FC<IvaWrapperProps> = (props: IvaWrapperProps) => {
    const personaMapped = useRef<boolean>(false);
    const [personaState, setPersonaState] = useState<PersonaState>({
        config: props.almeConfig || appConfigMain,
        theme: props.theme || appThemeMain,
        themeMode: props.themeMode || 'light',
        verintAccessKey: useRef<string>(''),
        currentLanguageFile: (defaultTranslation as Record<string, Record<string, string>>)["en-US"],
        currentLanguage: 'en-US',
    } as PersonaState);


    const setAlmePersonaWrapper = useCallback((newKey: string): void => {
        const setPersona = async () => {
            await setNewAccessKey(newKey, personaState, setPersonaState, personaMapped);
        };
        void setPersona();
    }, [personaState, setPersonaState, personaMapped]);

    const intl = setIntlConfig(personaState.currentLanguage, personaState.currentLanguageFile);

    // Handles all launch events not triggered via postMessage.
    useEffect(() => {
        launchWithPersona(getAccessKey, setPersonaState, setAlmePersonaWrapper, personaMapped, personaState, props);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangePersona = useGetPersonaConfigCallback(setAlmePersonaWrapper);
    useAlmeMessageListener(clientCommunicator, onChangePersona);

    const clearAppData = () => {
        savedAccessKey.ClearStorage();
    };

    const ivaType = props.ivaType || "embedded";
    const apiSettings = props.apiSettings || createDefaultSettings();

    if (props.renderTestPage) {
        return (
            <RawIntlProvider value={intl}>
                <AlmeTestPage personaState={personaState} setPersonaState={setPersonaState}
                    ivaType={ivaType} apiSettings={apiSettings} personaMapped={personaMapped}
                    isIvaProLaunch={props.isIvaProLaunch} clearAppData={clearAppData} />
            </RawIntlProvider>
        );
    }
    return (
        <RawIntlProvider value={intl}>
            <VerintIVAUI ivaType={ivaType} personaState={personaState} apiSettings={apiSettings}
                personaMapped={personaMapped} isIvaProLaunch={props.isIvaProLaunch} clearAppData={clearAppData} />
        </RawIntlProvider>
    );
};

export default IvaWrapper;

const setNewAccessKey = async (
    newKey: string,
    personaState: PersonaState,
    setPersonaState: React.Dispatch<React.SetStateAction<PersonaState>>,
    personaMapped: React.MutableRefObject<boolean>,
) => {
    let persona = personaState;
    if (personaState.verintAccessKey.current === newKey) {
        return;
    }
    try {
        // new AccessKey
        if (newKey !== "") {
            savedAccessKey.UpdateStorage(newKey);
            persona = await getPersonaFromMapping(newKey, personaState, personaMapped);
            setPersonaState(persona);
        }
    } catch (ex) {
        defaultLogger.error(ex);
    }
};

const launchWithPersona = (
    getAccessKey: (ivaType?: AlmeIvaTypeProp | undefined) => string | undefined,
    setPersonaState: React.Dispatch<React.SetStateAction<PersonaState>>,
    setAlmePersonaWrapper: (newKey: string) => void,
    personaMapped: React.MutableRefObject<boolean>,
    personaState: PersonaState,
    wrapperProps: IvaWrapperProps,
) => {
    try {
        const mapPersona = getAccessKey(wrapperProps.ivaType) ?? savedAccessKey.GetStorage() ?? wrapperProps.verintAccessKey;
        const isIvaProLaunch: boolean =
            wrapperProps.theme ?? wrapperProps.themeMode ?? wrapperProps.almeConfig ??
            wrapperProps.clientConfig ?? wrapperProps.apiSettings ?? wrapperProps.isIvaProLaunch
                ? true : false;
        if (mapPersona) {
            setAlmePersonaWrapper(mapPersona);
        }
        else if (isIvaProLaunch) {
            personaMapped.current = true;
            savedAccessKey.UpdateStorage(defaultAccessKey);
            setPersonaState({
                ...personaState,
                verintAccessKey: { current: defaultAccessKey },
            } as PersonaState);
        }
    } catch (ex) {
        defaultLogger.error(ex);
    }
};

